/**
 * Marketplace hash-routing + Grid↔Marketplace return-context (Macro #29 Stage 3).
 *
 * Routes (all under the shared hash router):
 *   #marketplace                       → Marketplace Home
 *   #marketplace/land[?filters]        → LAND tab
 *   #marketplace/artworks[?filters]    → ARTWORKS tab
 *   #marketplace/mine[?sub]            → MY MARKETPLACE
 *   #marketplace/owner                 → OWNER / ADMIN supply
 *   #marketplace/land/<orderHash>      → LAND asset detail
 *   #marketplace/artwork/<id>          → ARTWORK asset detail
 *
 * Filter/view state lives in the hash query so refresh / deep-link / BACK
 * behave professionally (no fragile single state blob).
 */
export type MarketplaceTab = "home" | "land" | "artworks" | "mine" | "owner";

export interface MarketplaceDetail {
  kind: "land" | "artwork";
  id: string;
}

export interface MarketplaceRouteState {
  tab: MarketplaceTab;
  detail: MarketplaceDetail | null;
  query: Record<string, string>;
}

function parseQuery(qs: string): Record<string, string> {
  const out: Record<string, string> = {};
  const sp = new URLSearchParams(qs);
  sp.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

/** Parse a hash route string (already stripped of leading '#'). */
export function parseMarketplaceRoute(route: string): MarketplaceRouteState | null {
  if (route !== "marketplace" && !route.startsWith("marketplace/") && !route.startsWith("marketplace?")) {
    return null;
  }
  const [pathPart, queryPart = ""] = route.split("?");
  const query = parseQuery(queryPart);
  const segs = pathPart.split("/").filter(Boolean); // ["marketplace", ...]
  const rest = segs.slice(1);
  if (rest.length === 0) return { tab: "home", detail: null, query };
  const head = rest[0];
  if (head === "land") {
    if (rest[1]) return { tab: "land", detail: { kind: "land", id: rest[1] }, query };
    return { tab: "land", detail: null, query };
  }
  if (head === "artworks" || head === "artwork") {
    if (head === "artwork" && rest[1]) return { tab: "artworks", detail: { kind: "artwork", id: rest[1] }, query };
    return { tab: "artworks", detail: null, query };
  }
  if (head === "mine") return { tab: "mine", detail: null, query };
  if (head === "owner") return { tab: "owner", detail: null, query };
  return { tab: "home", detail: null, query };
}

function buildQuery(query: Record<string, string>): string {
  const sp = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== "" && v != null) sp.set(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function marketplaceHash(tab: MarketplaceTab, query: Record<string, string> = {}, detail?: MarketplaceDetail): string {
  let path = "marketplace";
  if (detail) {
    path += detail.kind === "land" ? `/land/${detail.id}` : `/artwork/${detail.id}`;
  } else if (tab !== "home") {
    path += `/${tab}`;
  }
  return `#${path}${buildQuery(query)}`;
}

/** Navigate within the marketplace, replacing the hash. */
export function goMarketplace(tab: MarketplaceTab, query: Record<string, string> = {}, detail?: MarketplaceDetail): void {
  window.location.hash = marketplaceHash(tab, query, detail);
}

/** Merge a patch into the current marketplace query (drops empty values). */
export function patchMarketplaceQuery(current: MarketplaceRouteState, patch: Record<string, string>): void {
  const merged = { ...current.query, ...patch };
  Object.keys(merged).forEach((k) => {
    if (merged[k] === "" || merged[k] == null) delete merged[k];
  });
  window.location.hash = marketplaceHash(current.tab, merged, current.detail ?? undefined);
}

// ---- Grid ↔ Marketplace return context (§18) ----
const RETURN_KEY = "grid_mkt_return";
const SCROLL_KEY = "grid_mkt_scroll";

/** Called before VIEW ON GRID so BACK TO MARKETPLACE can restore exact state. */
export function stashMarketplaceReturn(): void {
  try {
    const hash = window.location.hash || "#marketplace";
    sessionStorage.setItem(RETURN_KEY, hash);
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY || 0));
  } catch {
    /* sessionStorage unavailable — return button simply won't show */
  }
}

export function hasMarketplaceReturn(): boolean {
  try {
    return !!sessionStorage.getItem(RETURN_KEY);
  } catch {
    return false;
  }
}

export function popMarketplaceReturn(): string | null {
  try {
    const hash = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return hash;
  } catch {
    return null;
  }
}

export function clearMarketplaceReturn(): void {
  try {
    sessionStorage.removeItem(RETURN_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
  } catch {
    /* noop */
  }
}

/** Pop the return context AND the stashed scroll position (clears both). */
export function consumeMarketplaceReturn(): { hash: string; scrollY: number } | null {
  try {
    const hash = sessionStorage.getItem(RETURN_KEY);
    if (!hash) return null;
    const scrollY = Number(sessionStorage.getItem(SCROLL_KEY) ?? "0") || 0;
    sessionStorage.removeItem(RETURN_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
    return { hash, scrollY };
  } catch {
    return null;
  }
}

/**
 * VIEW ON GRID — stash the current marketplace context, then deep-link the Grid
 * to center on a world point (the GridViewer parses `#grid?fx=&fy=&span=&aid=`).
 * `t` guarantees a real hash change even on repeated invocations.
 */
export function viewOnGrid(fx: number, fy: number, span: number, aid?: string): void {
  stashMarketplaceReturn();
  const parts = [`fx=${Math.round(fx)}`, `fy=${Math.round(fy)}`, `span=${Math.max(1, Math.round(span))}`];
  if (aid) parts.push(`aid=${encodeURIComponent(aid)}`);
  parts.push(`t=${Date.now()}`);
  window.location.hash = `grid?${parts.join("&")}`;
}
